import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BodyText, BodyText1Css, BodyText2Css, ButtonText1Css } from '@panel/commons/lib/typography';
import { getMainColor, getNeutralColors } from '@panel/commons/lib/theme';
import { sendCustomTrackingInfo } from '@panel/commons';
import { Icon } from '@panel/commons/lib/Icon/Icon';
import { createSvgComponent } from '@panel/commons/helpers/scoreHelpers';
import SimpleTag from '@panel/commons/lib/SimpleTag';
import { getParticularItems, getPublisherItems, getSeekerItems, mockUserType } from '../../../../../mockData';

import { ChrevonRigth } from '../../../../../assets';

const DropdownContainer = styled.div`
  background-color: ${getNeutralColors('white')};
  border-radius: 0px 0px 12px 12px;
  border: 1px solid ${getNeutralColors('grisClaroII')};
  display: flex;
  flex-direction: row;
  min-width: 240px;
  opacity: 0;
  overflow: hidden;
  padding: 24px 24px 8px;
  position: absolute;
  right: 0px;
  top: 60px;
  transform: translateY(-20px);
  transition: opacity 0.4s ease 0s, transform 0.4s ease 0s, visibility 0.4s ease 0s;
  visibility: hidden;
  z-index: 10000;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.05);

  ${props =>
    props.isActive &&
    css`
      opacity: 1;
      transform: translateY(0px);
      visibility: visible;
    `}
`;

const UserDataContainer = styled.div`
  border-bottom: 1px solid ${getNeutralColors('grisClaroII')};
  line-height: 0px;
  padding: 0px 0px 8px;
  margin-bottom: 8px;
  width: 242px;
`;

const LevelDataContainer = styled.div`
  line-height: 0px;
  padding: 0px 0px 8px;
  margin-bottom: 8px;
  width: 242px;
  display: flex;
  align-items: center; /* Alinea los elementos al centro verticalmente */
  justify-content: space-between; /* Distribuye espacio entre los elementos */
  cursor: pointer;
  height: 40px;
  :hover {
    background: ${getNeutralColors('grisClaroIII')};
  }
`;

const LevelDataWraper = styled.div`
  border-bottom: 1px solid ${getNeutralColors('grisClaroII')};
  line-height: 0px;
  padding: 0px 0px 8px;
  margin-bottom: 8px;
  width: 242px;
  align-items: center;
  cursor: pointer;
  height: ${({ isAdmin }) => (isAdmin ? '58px' : '48px')};
  :hover {
    background: ${getNeutralColors('grisClaroIII')};
  }
`;

const LevelDataDisclaimer = styled.div`
  line-height: 0px;
  padding: 0px 8px 8px;
  margin-bottom: 8px;
  width: 242px;
  display: flex;
  align-items: center; /* Alinea los elementos al centro verticalmente */
  justify-content: space-between; /* Distribuye espacio entre los elementos */
`;

const SpanLevel = styled.span`
  font-family: Hind;
  font-size: ${({ level }) => (level === 0 ? '16px' : '16px')};
  font-style: normal;
  font-weight: 600;
  padding-left: ${({ level }) => (level === 0 ? '0px' : '8px')};
  padding-top: 12px;
  color: ${({ level }) => (level === 0 ? '#727272' : '')};
`;
const ScoreContainer = styled.div`
  padding-left: 8px;
  padding-top: 12px;
`;

const ChevronContainer = styled.div`
  margin-right: 8px;
  padding-top: 12px;
`;

const UserDataName = styled.p`
  ${ButtonText1Css};
  line-height: 130%;
  color: ${getNeutralColors('black')};
  text-overflow: ellipsis;
  overflow: hidden;
  user-select: text;
`;

const UserDataSub = styled.p`
  ${BodyText1Css};
  font-weight: 400;
  line-height: normal;
  color: ${getNeutralColors('grisMedioII')};
  text-overflow: ellipsis;
  overflow: hidden;
  user-select: text;
`;

const LinksContainer = styled.div`
  width: 250px;
  .border {
    border-bottom: 1px solid ${getNeutralColors('grisClaroII')};
    margin: 8px 0px;
  }
`;

const BaseLink = css`
  color: ${getNeutralColors('black')};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  line-height: 25px;
  padding: 11px 0px;
  text-decoration: none;

  &:hover {
    background: ${getNeutralColors('grisClaroIII')};
    &.active {
    }
  }
  &.active {
  }
`;

const ExternalLink = styled.a`
  ${BaseLink};
  &.logoutBtn {
    padding-bottom: 9px;
  }
`;

const LinkItem = styled(NavLink)`
  ${BaseLink}
`;

const Subtitle = styled.span`
  ${BodyText2Css};
  font-weight: 400;
  color: ${getNeutralColors('grisMedioII')};
  display: block;
`;

const ItemName = styled(BodyText).attrs({ size: 100 })`
  line-height: 25.6px;
  margin-top: 3px;
  padding-left: 8px;
`;

const ItemNameHelp = styled(ItemName)`
  ${BaseLink};
  cursor: pointer;
`;

const Pill = styled.span`
  ${BodyText1Css};
  border-radius: 2px;
  color: ${getNeutralColors('white')};
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  margin-left: 8px;
  padding: 0px 3px;
  text-transform: uppercase;

  ${({ color }) =>
    color === 'gray'
      ? css`
          background: ${getNeutralColors('grisOscuro')};
        `
      : css`
          background: ${getMainColor()};
        `}
`;
function UserDropdown({ user, showDropdown, close, isPortalWithNewReputation, reputationLevelState }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const publisherType = mockUserType.filter(index => index.id === user.publisherTypeId);

  const isAdmin = !user.isSeeker && user.userTypeId === '1' && user.publisherId.toLowerCase() !== 'null';
  // eslint-disable-next-line no-nested-ternary
  const userType = user.isSeeker ? 'seeker' : user.publisherTypeId === '1' ? 'particular' : 'publisher';

  let items = [];
  if (user.publisherTypeId === '1' && !user.isSeeker) {
    items = getParticularItems('dropdown');
  } else if (user.publisherTypeId !== '1' && user.isSeeker) {
    items = getSeekerItems('dropdown');
  } else {
    items = getPublisherItems(user.userTypeId === '1' ? 'dropdownAdmin' : 'dropdown');
  }

  const userName = user.name ? `${user.name} ${user.lastName || ''}` : user.email.split('@')[0];

  const siteId = process.env.REACT_APP_BUILD_SITE;
  const isBrasilSite = ['WIBR', 'IWBR', 'CMBR'].includes(siteId);
  const SvgComponent = createSvgComponent(reputationLevelState.levelValue);

  // Se realiza este cambio para solucionar el Problema suscitado en PUER-5639
  const HelpButton = () => {
    const handleHelpClick = () => {
      window.location.href = t('panel.header.menufooter.helpUrl');
    };
    return (
      <ItemNameHelp className='actionBtnName' onClick={() => handleHelpClick()}>
        {t('panel.header.menufooter.help')}
      </ItemNameHelp>
    );
  };

  const handleOnClicVerReputacion = () => {
    sendCustomTrackingInfo({
      event: 'select_content',
      content_type: 'Menu hamburguesa',
      item_id: 'Nivel'
    });
    if (!pathname.includes('estadisticas')) {
      window.open(`${window.location.origin}${t('panel.reputacion.url')}`);
    }
  };

  return (
    <DropdownContainer isActive={showDropdown} onMouseLeave={close} id='header-dropdownMenu'>
      <LinksContainer>
        <UserDataContainer data-tour-panel='reputation-levelName'>
          <UserDataName>{userName}</UserDataName>
          <UserDataSub title={user.email} id='pubid'>
            {isAdmin ? t('panel.configuration.data.publisherId', { value: user.publisherId }) : user.email}
          </UserDataSub>
        </UserDataContainer>
        {isPortalWithNewReputation && user.publisherTypeId !== '1' && !user.isSeeker && (
          <LevelDataWraper level={reputationLevelState.levelValue || 0} isAdmin={isAdmin}>
            <LevelDataContainer onClick={handleOnClicVerReputacion} data-tour-panel='reputation-levelValue'>
              <SpanLevel level={reputationLevelState.levelValue || 0}>
                {reputationLevelState.levelValue
                  ? t(`panel.reputacion.scoring.level.${reputationLevelState.levelValue}`)
                  : t('panel.reputacion.scoring.level.0')}
              </SpanLevel>
              <ScoreContainer>
                <SvgComponent />
                {isBrasilSite && <SimpleTag text={t('panel.reputacion.badge.menu')} />}
              </ScoreContainer>

              <ChevronContainer>
                <ChrevonRigth />
              </ChevronContainer>
            </LevelDataContainer>
            {isAdmin && <LevelDataDisclaimer>{t('panel.reputacion.sucursales.text')}</LevelDataDisclaimer>}
          </LevelDataWraper>
        )}
        {items &&
          items.map(item =>
            item.subtitle ? (
              <Subtitle key={item.icon} data-tour-panel='reputation-level-II'>
                {t(item.itemName)}
              </Subtitle>
            ) : (
              ((item.icon === 'assessments' && user.showTasador) || item.icon !== 'assessments') &&
              (item.external ? (
                <div key={item.icon}>
                  <ExternalLink
                    onClick={close}
                    href={`${t(item.path)}${
                      user.userTypeId !== '1' && item.icon === 'realEstateBranch' ? user.publisherId : ''
                    }`}
                    rel='noopener noreferrer'
                    title={t(
                      publisherType.length && item.icon === 'realEstate' ? publisherType[0].title : item.itemName
                    )}
                    data-panel={`headerMenuItemDesk_${item.icon}`}
                    border={item.border && item.border.includes(userType) ? 1 : 0}
                  >
                    <Icon name={item.icon === 'realEstateBranch' ? 'realEstate' : item.icon} margin='0 0 0 8px' />
                    <ItemName className='actionBtnName'>
                      {t(publisherType.length && item.icon === 'realEstate' ? publisherType[0].title : item.itemName)}
                    </ItemName>
                  </ExternalLink>
                  {item.border && item.border.includes(userType) && <div className='border' />}
                </div>
              ) : (
                <div key={item.icon}>
                  <LinkItem
                    onClick={close}
                    to={t(item.path)}
                    title={t(
                      publisherType.length && item.icon === 'realEstate' ? publisherType[0].title : item.itemName
                    )}
                    activeClassName='active'
                    // Rebuscado, pero react-router-dom pasa los boolean como string
                    featured={item.featured ? 1 : 0}
                    data-panel={`headerMenuItemDesk_${item.icon}`}
                    border={item.border && item.border.includes(userType) ? 1 : 0}
                  >
                    <Icon name={item.icon} margin='0 0 0 8px' />
                    <ItemName className='actionBtnName'>
                      {t(publisherType.length && item.icon === 'realEstate' ? publisherType[0].title : item.itemName)}
                    </ItemName>
                    {item.pill && <Pill color={item.pill.color}>{t(item.pill.text)}</Pill>}
                  </LinkItem>
                  {item.border && item.border.includes(userType) && <div className='border' />}
                </div>
              ))
            )
          )}
        {siteId !== 'LLVE' && <HelpButton />}

        <ExternalLink
          href='/logout.bum'
          rel='noopener noreferrer'
          className='logoutBtn'
          data-panel='headerDesktopDropdown_logout'
        >
          <ItemName>{t('panel.header.menufooter.logout')}</ItemName>
        </ExternalLink>
      </LinksContainer>
    </DropdownContainer>
  );
}

UserDropdown.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  showDropdown: PropTypes.bool
}.isRequired;

export { UserDropdown };
